/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import DateTimePicker from "./date-time-picker";
import CustomSelect from "src/components/custom-select";
import { SearchIcon } from "lucide-react";
import { toast } from "react-toastify";
import CommoditiesTable from "./CommoditiesTable";
import { format } from "date-fns";
import AddressAutocomplete from "./shipper-autocomplete";
import { useQuote } from "src/contexts/QuoteContext";
import BillingPartyUpdateModal from "./BillingPartyUpdateModal";

// Constants
const TIME_OPTIONS = [
  "08:00",
  "08:15",
  "08:30",
  "08:45",
  "09:00",
  "09:15",
  "09:30",
  "09:45",
  "10:00",
  "10:15",
  "10:30",
  "10:45",
  "11:00",
  "11:15",
  "11:30",
  "11:45",
  "12:00",
  "12:15",
  "12:30",
  "12:45",
  "13:00",
  "13:15",
  "13:30",
  "13:45",
  "14:00",
  "14:15",
  "14:30",
  "14:45",
  "15:00",
  "15:15",
  "15:30",
  "15:45",
  "16:00",
  "16:15",
  "16:30",
  "16:45",
  "17:00",
  "17:15",
  "17:30",
  "17:45",
  "18:00",
  "18:15",
  "18:30",
  "18:45",
  "19:00",
  "19:15",
  "19:30",
  "19:45",
  "20:00",
  "20:15",
  "20:30",
  "20:45",
];

// Reusable Components
const FormTextField = ({
  name,
  label,
  value,
  onChange,
  required = false,
  placeholder = "",
  multiline = false,
  rows = 1,
  error = false,
  helperText = "",
  InputProps = {},
  ...props
}) => (
  <TextField
    required={required}
    fullWidth
    name={name}
    label={label}
    value={value || ""}
    onChange={onChange}
    placeholder={placeholder}
    multiline={multiline}
    rows={multiline ? rows : undefined}
    margin="normal"
    size="small"
    variant="outlined"
    sx={{ background: "#fff" }}
    InputLabelProps={{ sx: { fontSize: "14px !important" } }}
    error={error}
    helperText={helperText}
    InputProps={InputProps}
    {...props}
  />
);

const TimeSelectGrid = ({
  openingTimeValue,
  closingTimeValue,
  onOpeningTimeChange,
  onClosingTimeChange,
  fieldPrefix,
  touched,
  errors,
}) => (
  <Grid container spacing={2} sx={{ mt: "0px" }}>
    <Grid item xs={6}>
      <CustomSelect
        label="Opening time"
        name={`${fieldPrefix}.openingTime`}
        value={openingTimeValue || ""}
        onChange={onOpeningTimeChange}
        options={TIME_OPTIONS.map((time) => ({
          id: time,
          value: time,
          label: time,
        }))}
        size="small"
        sx={{ background: "#fff" }}
        error={touched?.openingTime && Boolean(errors?.openingTime)}
        errorMessage={touched?.openingTime && errors?.openingTime}
      />
    </Grid>
    <Grid item xs={6}>
      <CustomSelect
        label="Closing time"
        name={`${fieldPrefix}.closingTime`}
        value={closingTimeValue || ""}
        onChange={onClosingTimeChange}
        options={TIME_OPTIONS.map((time) => ({
          id: time,
          value: time,
          label: time,
        }))}
        size="small"
        sx={{ background: "#fff" }}
        error={touched?.closingTime && Boolean(errors?.closingTime)}
        errorMessage={touched?.closingTime && errors?.closingTime}
      />
    </Grid>
  </Grid>
);

const ContactFieldsGrid = ({
  values,
  onNestedChange,
  fieldPrefix,
  touched,
  errors,
}) => (
  <>
    <FormTextField
      name={`${fieldPrefix}.contactName`}
      label="Contact name"
      value={values?.contactName}
      onChange={(e) =>
        onNestedChange(fieldPrefix, "contactName", e.target.value)
      }
      placeholder="Full name"
      error={touched?.contactName && Boolean(errors?.contactName)}
      helperText={touched?.contactName && errors?.contactName}
    />
    <Grid container spacing={2}>
      <Grid item xs={8}>
        <FormTextField
          name={`${fieldPrefix}.contactPhone`}
          label="Phone"
          value={values?.contactPhone}
          onChange={(e) =>
            onNestedChange(fieldPrefix, "contactPhone", e.target.value)
          }
          placeholder="(000) 000-0000"
          error={touched?.contactPhone && Boolean(errors?.contactPhone)}
          helperText={touched?.contactPhone && errors?.contactPhone}
        />
      </Grid>
      <Grid item xs={4}>
        <FormTextField
          name={`${fieldPrefix}.contactExt`}
          label="Ext."
          value={values?.contactExt}
          onChange={(e) =>
            onNestedChange(fieldPrefix, "contactExt", e.target.value)
          }
          placeholder="Ext."
          error={touched?.contactExt && Boolean(errors?.contactExt)}
          helperText={touched?.contactExt && errors?.contactExt}
        />
      </Grid>
    </Grid>
    <FormTextField
      name={`${fieldPrefix}.contactEmail`}
      label="Contact email"
      value={values?.contactEmail}
      onChange={(e) =>
        onNestedChange(fieldPrefix, "contactEmail", e.target.value)
      }
      placeholder="Contact email"
      error={touched?.contactEmail && Boolean(errors?.contactEmail)}
      helperText={touched?.contactEmail && errors?.contactEmail}
    />
  </>
);

const AddressFieldsSection = ({
  values,
  onAddressChange,
  fieldPrefix,
  touched,
  errors,
}) => (
  <>
    <FormTextField
      required
      name={`${fieldPrefix}.addresses.address`}
      label="Address1"
      value={values?.addresses?.address}
      onChange={(e) => onAddressChange(fieldPrefix, "address", e.target.value)}
      error={touched?.addresses?.address && Boolean(errors?.addresses?.address)}
      helperText={touched?.addresses?.address && errors?.addresses?.address}
    />
    <FormTextField
      name={`${fieldPrefix}.addresses.address2`}
      label="Address2"
      value={values?.addresses?.address2}
      onChange={(e) => onAddressChange(fieldPrefix, "address2", e.target.value)}
      error={
        touched?.addresses?.address2 && Boolean(errors?.addresses?.address2)
      }
      helperText={touched?.addresses?.address2 && errors?.addresses?.address2}
    />
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormTextField
          required
          name={`${fieldPrefix}.addresses.city`}
          label="City"
          value={values?.addresses?.city}
          onChange={(e) => onAddressChange(fieldPrefix, "city", e.target.value)}
          error={touched?.addresses?.city && Boolean(errors?.addresses?.city)}
          helperText={touched?.addresses?.city && errors?.addresses?.city}
        />
      </Grid>
      <Grid item xs={6}>
        <FormTextField
          required
          name={`${fieldPrefix}.addresses.state`}
          label="State"
          value={values?.addresses?.state}
          onChange={(e) =>
            onAddressChange(fieldPrefix, "state", e.target.value)
          }
          error={touched?.addresses?.state && Boolean(errors?.addresses?.state)}
          helperText={touched?.addresses?.state && errors?.addresses?.state}
        />
      </Grid>
    </Grid>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormTextField
          required
          name={`${fieldPrefix}.addresses.country`}
          label="Country"
          value={values?.addresses?.country}
          onChange={(e) =>
            onAddressChange(fieldPrefix, "country", e.target.value)
          }
          error={
            touched?.addresses?.country && Boolean(errors?.addresses?.country)
          }
          helperText={touched?.addresses?.country && errors?.addresses?.country}
        />
      </Grid>
      <Grid item xs={6}>
        <FormTextField
          required
          name={`${fieldPrefix}.addresses.postal`}
          label="ZIP code"
          value={values?.addresses?.postal}
          onChange={(e) =>
            onAddressChange(fieldPrefix, "postal", e.target.value)
          }
          error={
            touched?.addresses?.postal && Boolean(errors?.addresses?.postal)
          }
          helperText={touched?.addresses?.postal && errors?.addresses?.postal}
        />
      </Grid>
    </Grid>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormTextField
          required
          name={`${fieldPrefix}.addresses.latitude`}
          label="Latitude"
          value={values?.addresses?.latitude}
          onChange={(e) =>
            onAddressChange(fieldPrefix, "latitude", e.target.value)
          }
          error={
            touched?.addresses?.latitude && Boolean(errors?.addresses?.latitude)
          }
          helperText={
            touched?.addresses?.latitude && errors?.addresses?.latitude
          }
        />
      </Grid>
      <Grid item xs={6}>
        <FormTextField
          required
          name={`${fieldPrefix}.addresses.longitude`}
          label="Longitude"
          value={values?.addresses?.longitude}
          onChange={(e) =>
            onAddressChange(fieldPrefix, "longitude", e.target.value)
          }
          error={
            touched?.addresses?.longitude &&
            Boolean(errors?.addresses?.longitude)
          }
          helperText={
            touched?.addresses?.longitude && errors?.addresses?.longitude
          }
        />
      </Grid>
    </Grid>
  </>
);

const PartySection = ({
  title,
  values,
  customers,
  onNestedChange,
  onAddressChange,
  setFieldValue,
  fieldName,
  touched,
  errors,
  children,
}) => {
  // Get the current orderStop data
  const orderStop = values?.orderStops?.[0] || {};
  const partyData = orderStop[fieldName] || {};

  return (
    <Grid item xs={12} md={4}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Quick search
      </Typography>
      <AddressAutocomplete
        customers={customers}
        values={partyData}
        handleNestedChange={onNestedChange}
        handleAddressChange={onAddressChange}
        setFieldValue={setFieldValue}
        fieldName={`orderStops[0].${fieldName}`}
      />
      <FormTextField
        required
        name={`orderStops[0].${fieldName}.name`}
        label="Company name"
        value={partyData?.name}
        onChange={(e) =>
          onNestedChange(`orderStops[0].${fieldName}`, "name", e.target.value)
        }
        error={touched?.[fieldName]?.name && Boolean(errors?.[fieldName]?.name)}
        helperText={touched?.[fieldName]?.name && errors?.[fieldName]?.name}
      />
      <AddressFieldsSection
        values={partyData}
        onAddressChange={onAddressChange}
        fieldPrefix={`orderStops[0].${fieldName}`}
        touched={touched?.[fieldName]}
        errors={errors?.[fieldName]}
      />
      <FormTextField
        name={`orderStops[0].${fieldName}.notes`}
        label={`${title} notes`}
        value={partyData?.notes}
        onChange={(e) =>
          onNestedChange(`orderStops[0].${fieldName}`, "notes", e.target.value)
        }
        multiline
        rows={4}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <InfoOutlinedIcon color="disabled" fontSize="small" />
            </InputAdornment>
          ),
        }}
        error={
          touched?.[fieldName]?.notes && Boolean(errors?.[fieldName]?.notes)
        }
        helperText={touched?.[fieldName]?.notes && errors?.[fieldName]?.notes}
      />
      <TimeSelectGrid
        openingTimeValue={partyData?.openingTime}
        closingTimeValue={partyData?.closingTime}
        onOpeningTimeChange={(field, value) =>
          onNestedChange(`orderStops[0].${fieldName}`, "openingTime", value)
        }
        onClosingTimeChange={(field, value) =>
          onNestedChange(`orderStops[0].${fieldName}`, "closingTime", value)
        }
        fieldPrefix={`orderStops[0].${fieldName}`}
        touched={touched?.[fieldName]}
        errors={errors?.[fieldName]}
      />
      <ContactFieldsGrid
        values={partyData}
        onNestedChange={onNestedChange}
        fieldPrefix={`orderStops[0].${fieldName}`}
        touched={touched?.[fieldName]}
        errors={errors?.[fieldName]}
      />
      {children}
    </Grid>
  );
};

const TimePickerSection = ({
  title,
  isAppointment,
  values,
  setFieldValue,
  startTimeField,
  endTimeField,
  appointmentField,
  onAppointmentChange,
  touched,
  errors,
  required,
}) => {
  const orderStop = values?.orderStops?.[0] || {};

  return (
    <>
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        gutterBottom
        sx={{ mt: 2 }}
      >
        {title}
      </Typography>
      <DateTimePicker
        variant="outlined"
        label={"Start time"}
        required={required}
        value={
          orderStop[startTimeField]
            ? format(new Date(orderStop[startTimeField]), "MM/dd/yyyy hh:mm a")
            : null
        }
        onChange={(value) => {
          if (!value) return;
          const startTime = new Date(value);
          setFieldValue(`orderStops[0].${startTimeField}`, startTime);
          setFieldValue(`orderStops[0].${endTimeField}`, startTime);
        }}
        name={`orderStops[0].${startTimeField}`}
        sx={{ mb: 1 }}
        error={touched?.[startTimeField] && Boolean(errors?.[startTimeField])}
        helperText={touched?.[startTimeField] && errors?.[startTimeField]}
        shouldDisableDate={(date) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date < today;
        }}
      />
      <DateTimePicker
        variant="outlined"
        label={
          "End time"
          // isAppointment
          //   ? `${title.split(' ')[0]} end time`
          //   : `${title} end time`
        }
        required={required}
        value={
          orderStop[endTimeField]
            ? format(new Date(orderStop[endTimeField]), "MM/dd/yyyy hh:mm a")
            : ""
        }
        onChange={(value) =>
          setFieldValue(`orderStops[0].${endTimeField}`, value)
        }
        name={`orderStops[0].${endTimeField}`}
        // sx={{ mb: 2 }}
        error={touched?.[endTimeField] && Boolean(errors?.[endTimeField])}
        errorMessage={touched?.[endTimeField] && errors?.[endTimeField]}
        shouldDisableDate={(date) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date < today;
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            name={`orderStops[0].${appointmentField}`}
            checked={orderStop[appointmentField] || false}
            onChange={onAppointmentChange}
            sx={{ my: 0, py: 0 }}
            size="small"
          />
        }
        label="Make this an appointment"
        sx={{ mt: 1 }}
      />
    </>
  );
};

const AccessorialDropdown = ({
  showDropdown,
  setShowDropdown,
  searchTerm,
  setSearchTerm,
  accessorialsData,
  values,
  setFieldValue,
}) => (
  <Box sx={{ position: "relative" }}>
    <Button
      startIcon={<AddIcon />}
      variant="outlined"
      sx={{
        textTransform: "none",
        color: "primary.main",
        borderColor: "primary.main",
        borderRadius: 1,
        px: 3,
        py: 0.5,
      }}
      onClick={() => setShowDropdown(!showDropdown)}
    >
      Add accessorial
    </Button>
    {showDropdown && (
      <Paper
        elevation={3}
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          width: 350,
          maxHeight: 400,
          mt: 1,
          zIndex: 1000,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            fullWidth
            placeholder="Search accessorials"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ overflowY: "auto", maxHeight: 300 }}>
          {(() => {
            // Use orderStop-level accessorials for selection and display
            const orderStopAccessorials = Array.isArray(
              values?.orderStops?.[0]?.accessorials,
            )
              ? values?.orderStops?.[0]?.accessorials
              : [];
            const orderStopAccessorialUuids = orderStopAccessorials.map(
              (acc) => acc.uuid,
            );

            return accessorialsData
              ?.filter(
                (acc) =>
                  acc.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                  !orderStopAccessorialUuids.includes(acc.uuid),
              )
              .map((accessorial) => (
                <Box
                  key={accessorial.uuid}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  onClick={() => {
                    const currentOrderStopAccessorials =
                      values?.orderStops?.[0]?.accessorials || [];
                    const newAccessorials = [
                      ...currentOrderStopAccessorials,
                      accessorial,
                    ];
                    setFieldValue(
                      "orderStops[0].accessorials",
                      newAccessorials,
                    );
                    // Also update the accessorialUuids array to match
                    const newAccessorialUuids = newAccessorials.map(
                      (acc) => acc.uuid,
                    );
                    setFieldValue(
                      "orderStops[0].accessorialUuids",
                      newAccessorialUuids,
                    );
                    setShowDropdown(false);
                    setSearchTerm("");
                  }}
                >
                  <Typography variant="body2">{accessorial.name}</Typography>
                </Box>
              ));
          })()}
        </Box>
      </Paper>
    )}
  </Box>
);

export default function OrderForm({
  values,
  setFieldValue,
  detailsRef,
  commoditiesRef,
  shipperCustomers,
  consigneeCustomers,
  billingCustomers,
  accessorialsData,
  orderId,
  touched,
  errors,
  refetchOrder,
}) {
  const [showAccessorialDropdown, setShowAccessorialDropdown] = useState(false);
  const [accessorialSearchTerm, setAccessorialSearchTerm] = useState("");
  const [openBillingModal, setOpenBillingModal] = useState(false);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [showBillingPartyModal, setShowBillingPartyModal] = useState(false);
  const { quoteData } = useQuote();

  // Initialize orderStops if it doesn't exist
  if (!values?.orderStops || values?.orderStops?.length === 0) {
    setFieldValue("orderStops", [{}]);
  }

  // Ensure there's at least one commodity row for the first stop when creating a new order
  if (
    values?.orderStops &&
    Array.isArray(values.orderStops) &&
    values.orderStops.length > 0
  ) {
    const firstStop = values.orderStops[0] || {};
    if (
      !Array.isArray(firstStop.commodities) ||
      firstStop.commodities.length === 0
    ) {
      setFieldValue("orderStops[0].commodities", [
        {
          descriptions: "",
          quantity: null,
          lengthValue: null,
          width: null,
          height: null,
          pieces: null,
          lf: null,
          weight: null,
          class: "",
          typeUuid: "",
          _action: "create",
        },
      ]);
    }
  }

  // Helper functions
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldValue(name, value);
  };

  const handleNestedChange = (section, field, value) => {
    setFieldValue(`${section}.${field}`, value);
  };

  const handleAddressChange = (section, field, value) => {
    setFieldValue(`${section}.addresses.${field}`, value);
  };

  const hasValidCoordinates = () => {
    const orderStop = values?.orderStops?.[0] || {};
    const shipperLat = orderStop?.shipper?.addresses?.latitude;
    const shipperLng = orderStop?.shipper?.addresses?.longitude;
    const consigneeLat = orderStop?.consignee?.addresses?.latitude;
    const consigneeLng = orderStop?.consignee?.addresses?.longitude;

    return (
      shipperLat &&
      shipperLng &&
      consigneeLat &&
      consigneeLng &&
      !isNaN(shipperLat) &&
      !isNaN(shipperLng) &&
      !isNaN(consigneeLat) &&
      !isNaN(consigneeLng)
    );
  };

  const calculateDropOffDistancesMapbox = async (
    apiKey,
    origin,
    destination,
  ) => {
    try {
      // Validate inputs
      if (!apiKey) throw new Error("Mapbox API key is required");
      if (!origin || !origin.longitude || !origin.latitude)
        throw new Error("Origin coordinates are required");
      if (!destination || !destination.longitude || !destination.latitude)
        throw new Error("Destination coordinates are required");

      // Format coordinates for Mapbox API: longitude,latitude
      const coordinates = `${origin.longitude},${origin.latitude};${destination.longitude},${destination.latitude}`;

      // Call Mapbox Directions API
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}?access_token=${apiKey}&geometries=geojson`,
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Mapbox API error (${response.status}): ${errorText}`);
      }

      const data = await response.json();

      if (!data.routes || data.routes.length === 0) {
        throw new Error("No route found between these locations");
      }

      // Distance is in meters, convert to miles
      const distanceInMeters = data.routes[0].distance;
      const distanceInMiles = distanceInMeters / 1609.34;

      return {
        totalDistance: distanceInMiles,
        route: data.routes[0],
        duration: data.routes[0].duration, // Duration in seconds
        geometry: data.routes[0].geometry, // GeoJSON LineString of the route
      };
    } catch (error) {
      console.error("Error in calculateDropOffDistancesMapbox:", error);
      throw error;
    }
  };

  const calculateDistance = async () => {
    if (!hasValidCoordinates()) {
      toast.error("Valid shipper and consignee coordinates are required");
      return;
    }

    try {
      const apiKey =
        "sk.eyJ1IjoiY29tcHUtdnNpb24iLCJhIjoiY20wZHU1bW4wMGYwNTJsczE4M2g4dTN6OCJ9.DoDyF9sdK1rGeE2Rc0VFWw";

      const orderStop = values?.orderStops?.[0] || {};
      const shipperLng = parseFloat(orderStop?.shipper?.addresses?.longitude);
      const shipperLat = parseFloat(orderStop?.shipper?.addresses?.latitude);
      const consigneeLng = parseFloat(
        orderStop?.consignee?.addresses?.longitude,
      );
      const consigneeLat = parseFloat(
        orderStop?.consignee?.addresses?.latitude,
      );

      // Call the utility function with the required parameters
      const { totalDistance } = await calculateDropOffDistancesMapbox(
        apiKey,
        { longitude: shipperLng, latitude: shipperLat },
        { longitude: consigneeLng, latitude: consigneeLat },
      );

      // Convert to miles if needed and round
      const distanceInMiles = Math.round(totalDistance);

      // Update the form field
      setFieldValue("orderStops[0].billingMiles", distanceInMiles);
      toast.success(`Distance calculated: ${distanceInMiles} miles`);
    } catch (error) {
      console.error("Error calculating distance:", error);
      toast.error(
        "Failed to calculate distance. Please check coordinates and try again.",
      );
    }
  };

  const handleAppointmentChange = (appointmentType, accessorialName) => (e) => {
    const isChecked = e.target.checked;
    // Helper to find accessorials by loose name match (case-insensitive, substring)
    const findAccessorialByName = (name) =>
      accessorialsData?.find(
        (accessorial) =>
          !!accessorial?.name &&
          accessorial.name.toLowerCase().includes(name.toLowerCase()),
      );

    const appointmentAccessorial = findAccessorialByName(accessorialName);
    // Determine attempted accessorial based on appointment type (pickup vs delivery)
    let attemptedAccessorial = null;
    if (appointmentType.toLowerCase().includes("pickup")) {
      attemptedAccessorial =
        findAccessorialByName("attempted pick") ||
        findAccessorialByName("attempted pickup") ||
        findAccessorialByName("attempted pick up");
    } else if (appointmentType.toLowerCase().includes("delivery")) {
      attemptedAccessorial =
        findAccessorialByName("attempted deliver") ||
        findAccessorialByName("attempted delivery") ||
        findAccessorialByName("attempted drop");
    }

    // Start from current arrays (make copies)
    let currentAccessorials = Array.isArray(
      values?.orderStops?.[0]?.accessorials,
    )
      ? [...values.orderStops[0].accessorials]
      : [];
    let currentAccessorialUuids = Array.isArray(
      values?.orderStops?.[0]?.accessorialUuids,
    )
      ? [...values.orderStops[0].accessorialUuids]
      : [];

    const addAccessorial = (acc) => {
      if (!acc) return;
      if (!currentAccessorialUuids.includes(acc.uuid)) {
        currentAccessorialUuids.push(acc.uuid);
      }
      if (!currentAccessorials.find((a) => a.uuid === acc.uuid)) {
        currentAccessorials.push(acc);
      }
    };

    const removeAccessorial = (acc) => {
      if (!acc) return;
      currentAccessorialUuids = currentAccessorialUuids.filter(
        (u) => u !== acc.uuid,
      );
      currentAccessorials = currentAccessorials.filter(
        (a) => a.uuid !== acc.uuid,
      );
    };

    if (isChecked) {
      // Add the appointment accessorial
      if (appointmentAccessorial) addAccessorial(appointmentAccessorial);

      // For pickup appointments, add attempted-pickup accessorial
      if (
        appointmentType.toLowerCase().includes("pickup") &&
        attemptedAccessorial
      ) {
        addAccessorial(attemptedAccessorial);
      }

      // For delivery appointments, add attempted-delivery accessorial
      // REMOVED: This was adding an extra accessorial that caused the issue
      // if (appointmentType.toLowerCase().includes("delivery") && attemptedAccessorial) {
      //   addAccessorial(attemptedAccessorial);
      // }
    } else {
      // remove them when disabling appointment
      if (appointmentAccessorial) removeAccessorial(appointmentAccessorial);
      if (attemptedAccessorial) removeAccessorial(attemptedAccessorial);
    }

    setFieldValue("orderStops[0].accessorials", currentAccessorials);
    setFieldValue("orderStops[0].accessorialUuids", currentAccessorialUuids);

    setFieldValue(`orderStops[0].${appointmentType}`, isChecked);
    // Handle time field swapping logic here
  };

  useEffect(() => {
    const alreadyPopulated = Boolean(values?.orderStops?.[0]?.shipper?.name);
    const data = {
      shipper: {
        name: quoteData?.origin?.name || "",
        addresses: {
          address: quoteData?.origin?.address || "",
          address2: quoteData?.origin?.address2 || "",
          city: quoteData?.origin?.city || "",
          state: quoteData?.origin?.state || "",
          postal: quoteData?.origin?.postal || "",
          country: quoteData?.origin?.country || "",
          latitude: quoteData?.origin?.latitude || "",
          longitude: quoteData?.origin?.longitude || "",
        },
      },
      consignee: {
        name: quoteData?.destination?.name || "",
        addresses: {
          address: quoteData?.destination?.address || "",
          address2: quoteData?.destination?.address2 || "",
          city: quoteData?.destination?.city || "",
          state: quoteData?.destination?.state || "",
          postal: quoteData?.destination?.postal || "",
          country: quoteData?.destination?.country || "",
          latitude: quoteData?.destination?.latitude || "",
          longitude: quoteData?.destination?.longitude || "",
        },
      },
      commodities: [
        {
          class: "50",
          descriptions: "FAK",
          height: quoteData?.height || 0,
          lengthValue: quoteData?.lengthValue || 0,
          lf: quoteData?.lf || 0,
          pieces: quoteData?.pieces || 0,
          quantity: quoteData?.quantity || 0,
          weight: quoteData?.weight || 0,
          width: quoteData?.width || 0,
          typeUuid: quoteData?.typeUuid || "",
          _action: "create",
        },
      ],
      accessorialUuids: quoteData?.accessorialUuids || [],
    };
    if (!alreadyPopulated && quoteData?.dimWeight && values?.orderStops?.[0]) {
      // build `data` …

      setFieldValue("orderStops[0].shipper", data.shipper);
      setFieldValue("orderStops[0].consignee", data.consignee);
      setFieldValue("orderStops[0].legs[0].commodities", data.commodities);
      setFieldValue("orderStops[0].accessorialUuids", data.accessorialUuids);
    }
  }, [quoteData, values?.orderStops?.[0]?.shipper?.name, setFieldValue]);
  const orderStop = values?.orderStops?.[0] || {};

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={4}>
        {/* Shipper Section */}
        <PartySection
          title="Shipper"
          values={values}
          customers={shipperCustomers}
          onNestedChange={handleNestedChange}
          onAddressChange={handleAddressChange}
          setFieldValue={setFieldValue}
          fieldName="shipper"
          touched={touched?.orderStops?.[0]}
          errors={errors?.orderStops?.[0]}
        >
          <TimePickerSection
            title={
              orderStop?.isAppointmentPickup ? "Pickup time" : "Ready time"
            }
            required={true}
            isAppointment={orderStop?.isAppointmentPickup}
            values={values}
            setFieldValue={setFieldValue}
            startTimeField={
              orderStop?.isAppointmentPickup
                ? "pickUpTimeStart"
                : "readyTimeStart"
            }
            endTimeField={
              orderStop?.isAppointmentPickup ? "pickUpTimeEnd" : "readyTimeEnd"
            }
            appointmentField="isAppointmentPickup"
            onAppointmentChange={handleAppointmentChange(
              "isAppointmentPickup",
              "appointment pickup",
            )}
            touched={touched?.orderStops?.[0]}
            errors={errors?.orderStops?.[0]}
          />
          {orderStop?.isAppointmentPickup && (
            <FormControlLabel
              control={
                <Checkbox
                  name="orderStops[0].isAppointmentConfirmPickup"
                  checked={orderStop?.isAppointmentConfirmPickup || false}
                  onChange={(e) =>
                    setFieldValue(
                      "orderStops[0].isAppointmentConfirmPickup",
                      e.target.checked,
                    )
                  }
                  size="small"
                  sx={{ my: 0, py: 0 }}
                />
              }
              label="Appointment confirmed"
              sx={{ mt: 1 }}
            />
          )}
        </PartySection>
        <PartySection
          title="Consignee"
          values={values}
          customers={consigneeCustomers}
          onNestedChange={handleNestedChange}
          onAddressChange={handleAddressChange}
          setFieldValue={setFieldValue}
          fieldName="consignee"
          touched={touched?.orderStops?.[0]}
          errors={errors?.orderStops?.[0]}
        >
          <TimePickerSection
            title={
              orderStop?.isAppointmentDelivery
                ? "Delivery time"
                : "Requested time"
            }
            isAppointment={orderStop?.isAppointmentDelivery}
            values={values}
            setFieldValue={setFieldValue}
            // startTimeField={
            //   orderStop?.isAppointmentDelivery
            //     ? "deliveryTimeStart"
            //     : "requestedTimeStart"
            // }
            // endTimeField={
            //   orderStop?.isAppointmentDelivery
            //     ? "deliveryTimeEnd"
            //     : "requestedTimeEnd"
            // }
            startTimeField={"requestedTimeStart"}
            endTimeField={"requestedTimeEnd"}
            appointmentField="isAppointmentDelivery"
            onAppointmentChange={handleAppointmentChange(
              "isAppointmentDelivery",
              "appointment delivery",
            )}
            touched={touched?.orderStops?.[0]}
            errors={errors?.orderStops?.[0]}
          />
          {orderStop?.isAppointmentDelivery && (
            <FormControlLabel
              control={
                <Checkbox
                  name="orderStops[0].isAppointmentConfirmDelivery"
                  checked={orderStop?.isAppointmentConfirmDelivery || false}
                  onChange={(e) =>
                    setFieldValue(
                      "orderStops[0].isAppointmentConfirmDelivery",
                      e.target.checked,
                    )
                  }
                  size="small"
                  sx={{ my: 0, py: 0 }}
                />
              }
              label="Appointment confirmed"
              sx={{ mt: 1 }}
            />
          )}
        </PartySection>

        {/* Billing Party Section */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Billing party
            </Typography>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              onClick={() => setOpenBillingModal(true)}
            >
              Edit
            </Button>
          </Box>

          {!showBillingForm ? (
            <>
              <Typography variant="body1" gutterBottom>
                {values.billingParty?.name || ""}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {values.billingParty?.addresses?.address || ""}
              </Typography>
              <Typography variant="body2" color="primary" gutterBottom>
                {`${values.billingParty?.addresses?.city || ""}, ${
                  values.billingParty?.addresses?.state || ""
                }, ${values.billingParty?.addresses?.country || ""} ${
                  values.billingParty?.addresses?.postal || ""
                }`}
              </Typography>
            </>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quick search
              </Typography>
              <AddressAutocomplete
                customers={billingCustomers}
                values={values.billingParty}
                handleNestedChange={handleNestedChange}
                handleAddressChange={handleAddressChange}
                setFieldValue={setFieldValue}
                fieldName="billingParty"
              />
              <FormTextField
                required
                name="billingParty.name"
                label="Company name"
                value={values.billingParty?.name}
                onChange={(e) =>
                  handleNestedChange("billingParty", "name", e.target.value)
                }
                error={
                  touched?.billingParty?.name &&
                  Boolean(errors?.billingParty?.name)
                }
                helperText={
                  touched?.billingParty?.name && errors?.billingParty?.name
                }
              />
              <AddressFieldsSection
                values={values.billingParty}
                onAddressChange={handleAddressChange}
                fieldPrefix="billingParty"
                touched={touched?.billingParty}
                errors={errors?.billingParty}
              />
              <TimeSelectGrid
                openingTimeValue={values.billingParty?.openingTime}
                closingTimeValue={values.billingParty?.closingTime}
                onOpeningTimeChange={(field, value) =>
                  handleNestedChange("billingParty", "openingTime", value)
                }
                onClosingTimeChange={(field, value) =>
                  handleNestedChange("billingParty", "closingTime", value)
                }
                fieldPrefix="billingParty"
                touched={touched?.billingParty}
                errors={errors?.billingParty}
              />
              <ContactFieldsGrid
                values={values.billingParty}
                onNestedChange={handleNestedChange}
                fieldPrefix="billingParty"
                touched={touched?.billingParty}
                errors={errors?.billingParty}
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowBillingForm(false)}
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          <Typography
            variant="subtitle1"
            fontWeight="bold"
            gutterBottom
            sx={{ mt: 4 }}
          >
            Additional information
          </Typography>
          {[
            { name: "customsBroker", label: "Customs broker" },
            { name: "portOfEntry", label: "Port of entry" },
            { name: "poNumber", label: "PO Number" },
            { name: "refNumber", label: "REF Number" },
            { name: "tenderNumber", label: "Load Tender Number" },
            { name: "customNumber", label: "Location In Warehouse" },
          ].map((field) => (
            <FormTextField
              key={field.name}
              name={`orderStops[0].${field.name}`}
              label={field.label}
              value={values.orderStops?.[0]?.[field.name]}
              onChange={handleChange}
              // error={
              //   touched?.orderStops?.[0]?.[field.name] &&
              //   Boolean(errors?.orderStops?.[0]?.[field.name])
              // }
              // helperText={
              //   touched?.orderStops?.[0]?.[field.name] &&
              //   errors?.orderStops?.[0]?.[field.name]
              // }
            />
          ))}
          <FormTextField
            name="orderStops[0].declaredValue"
            label="Declared value"
            value={values.orderStops?.[0]?.declaredValue}
            onChange={handleChange}
            placeholder="0.00"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            error={
              touched?.orderStops?.[0]?.declaredValue &&
              Boolean(errors?.orderStops?.[0]?.declaredValue)
            }
            helperText={
              touched?.orderStops?.[0]?.declaredValue &&
              errors?.orderStops?.[0]?.declaredValue
            }
          />
        </Grid>
      </Grid>

      {/* Special Instructions */}
      <Box sx={{ mt: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
        <Typography variant="subtitle1" gutterBottom>
          Special instructions
        </Typography>
        <FormTextField
          name="orderStops[0].instructions"
          value={values.orderStops?.[0]?.instructions}
          onChange={handleChange}
          placeholder="ie. This shipment is fragile. Please handle with care."
          multiline
          rows={4}
          error={
            touched?.orderStops?.[0]?.instructions &&
            Boolean(errors?.orderStops?.[0]?.instructions)
          }
          helperText={
            touched?.orderStops?.[0]?.instructions &&
            errors?.orderStops?.[0]?.instructions
          }
        />
      </Box>

      {/* Accessorials */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Accessorials
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {(() => {
            // Use orderStop-level accessorials (objects) for display
            const orderStopAccessorials = Array.isArray(orderStop?.accessorials)
              ? orderStop.accessorials
              : [];

            return orderStopAccessorials?.map((accessorial) => {
              if (!accessorial) return null;
              return (
                <Chip
                  key={accessorial.uuid}
                  label={accessorial.name}
                  onDelete={() => {
                    const newAccessorials =
                      orderStop?.accessorials?.filter(
                        (acc) => acc.uuid !== accessorial.uuid,
                      ) || [];
                    setFieldValue(
                      "orderStops[0].accessorials",
                      newAccessorials,
                    );
                    // Also update the accessorialUuids array to match
                    const newAccessorialUuids = newAccessorials.map(
                      (acc) => acc.uuid,
                    );
                    setFieldValue(
                      "orderStops[0].accessorialUuids",
                      newAccessorialUuids,
                    );
                  }}
                  sx={{ m: 0.5 }}
                />
              );
            });
          })()}
        </Box>
        <AccessorialDropdown
          showDropdown={showAccessorialDropdown}
          setShowDropdown={setShowAccessorialDropdown}
          searchTerm={accessorialSearchTerm}
          setSearchTerm={setAccessorialSearchTerm}
          accessorialsData={accessorialsData}
          values={values}
          setFieldValue={setFieldValue}
        />
      </Box>

      {/* Billable Miles */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Billable miles
        </Typography>
        <TextField
          name="orderStops[0].billingMiles"
          value={orderStop?.billingMiles || ""}
          onChange={handleChange}
          placeholder="0"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ width: "200px", background: "#fff" }}
          error={
            touched?.orderStops?.[0]?.billingMiles &&
            Boolean(errors?.orderStops?.[0]?.billingMiles)
          }
          helperText={
            touched?.orderStops?.[0]?.billingMiles &&
            errors?.orderStops?.[0]?.billingMiles
          }
        />
        <Button
          sx={{ textTransform: "none", color: "primary.main", ml: 2, mt: 2 }}
          onClick={calculateDistance}
          disabled={!hasValidCoordinates()}
        >
          Estimate with Google
        </Button>
      </Box>

      {/* Commodities Table */}
      <Box sx={{ mt: 4 }} ref={commoditiesRef}>
        <CommoditiesTable
          values={values || []}
          commodities={orderStop?.commodities || []}
          onAddCommodity={() => {
            const newCommodity = {
              descriptions: "",
              quantity: null,
              lengthValue: null,
              width: null,
              height: null,
              pieces: null,
              lf: null,
              weight: null,
              dim: null,
              class: "",
              typeUuid: "",
              orderUuid: orderId,
              _action: "create",
            };
            setFieldValue("orderStops[0].commodities", [
              ...(orderStop?.commodities || []),
              newCommodity,
            ]);
          }}
          onRemoveCommodity={(indexToRemove) => {
            const commodity = orderStop?.commodities?.[indexToRemove];
            if (!commodity) return;
            if (commodity.uuid) {
              const updatedCommodities = [...(orderStop?.commodities || [])];
              updatedCommodities[indexToRemove] = {
                uuid: commodity.uuid,
                _action: "delete",
              };
              setFieldValue("orderStops[0].commodities", updatedCommodities);
            } else {
              const updatedCommodities = (orderStop?.commodities || []).filter(
                (_, index) => index !== indexToRemove,
              );
              setFieldValue("orderStops[0].commodities", updatedCommodities);
            }
          }}
          onChangeCommodity={(index, field, value) => {
            setFieldValue(
              `orderStops[0].commodities[${index}].${field}`,
              value,
            );
            const currentCommodity =
              values?.orderStops?.[0]?.commodities?.[index];
            if (
              currentCommodity?.uuid &&
              currentCommodity?._action !== "create" &&
              currentCommodity?._action !== "delete"
            ) {
              setFieldValue(
                `orderStops[0].commodities[${index}]._action`,
                "update",
              );
            }
          }}
          serviceUuid={orderStop?.serviceUuids?.[0] || ""}
          onChangeService={(value) =>
            setFieldValue("orderStops[0].serviceUuids[0]", value)
          }
          classes={[
            "50",
            "55",
            "60",
            "65",
            "70",
            "77.5",
            "85",
            "92.5",
            "100",
            "110",
            "125",
            "150",
            "175",
            "200",
            "250",
            "300",
            "400",
            "500",
          ]}
          showServiceType={true}
          required={true}
          touched={touched?.orderStops?.[0]}
          errors={errors?.orderStops?.[0]}
          orderId={orderId}
        />
      </Box>

      {/* Billing Modal */}
      <Dialog
        open={openBillingModal}
        onClose={() => setOpenBillingModal(false)}
      >
        <DialogTitle>{"Editing billing address"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changing the billing party will update the associated customer for
            this order and generate a new quote. Any unsaved changes will be
            lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBillingModal(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setOpenBillingModal(false);
              setShowBillingPartyModal(true);
            }}
            color="primary"
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Billing Party Update Modal */}
      <BillingPartyUpdateModal
        open={showBillingPartyModal}
        onClose={() => setShowBillingPartyModal(false)}
        orderUuid={orderId}
        onSuccess={() => {
          setShowBillingPartyModal(false);
          if (refetchOrder) refetchOrder();
        }}
      />
    </Box>
  );
}
